import React from "react";

/**
 * ProMoInstructions — a sharp, numbered instruction panel used on ProMo
 * surfaces to fill the space where the mascot lives in Playful mode.
 *
 * Reads:
 *   <ProMoInstructions
 *     tag="Sign in"
 *     title="Get back to it."
 *     steps={[
 *       "Sign in with your existing credentials.",
 *       "Pick up where you left off — all history is saved.",
 *       "Need an account? Create one in 30 seconds.",
 *     ]}
 *   />
 *
 * Visual identity matches the "Why ProMo" hero panel: navy bg, gold tag,
 * burnt-orange numbered markers, restrained mono body copy.
 */
export default function ProMoInstructions({
  tag,
  title,
  intro,
  steps = [],
  footnote,
  className = "",
  size = "default", // "default" | "compact"
  fullHeight = false,
  testId,
}) {
  const padding = size === "compact" ? "p-6" : "p-7 md:p-8";
  const heightCls = fullHeight ? "h-full flex flex-col" : "";
  return (
    <div
      className={`rm-card-static ${padding} w-full max-w-md ${heightCls} ${className}`}
      style={{ background: "#0F1B2D", color: "white" }}
      data-testid={testId || "promo-instructions"}
    >
      {tag && (
        <div className="rm-label text-sm mb-3" style={{ color: "#FFC145" }}>
          {tag}
        </div>
      )}
      {title && (
        <h3 className="font-display text-xl md:text-2xl uppercase leading-tight mb-4">
          {title}
        </h3>
      )}
      {intro && (
        <p className="text-sm md:text-base mb-5" style={{ color: "#CBD5E1" }}>
          {intro}
        </p>
      )}
      {steps.length > 0 && (
        <ol className={`space-y-3 font-mono-rm ${fullHeight ? "flex-1" : ""}`}>
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="rm-text-orange font-display text-xl md:text-2xl leading-none flex-shrink-0"
                style={{ minWidth: "1.6em" }}
                aria-hidden="true"
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-sm md:text-base leading-relaxed" style={{ color: "#E2E8F0" }}>{s}</span>
            </li>
          ))}
        </ol>
      )}
      {footnote && (
        <div
          className="mt-5 pt-4 font-mono-rm text-xs md:text-sm"
          style={{ borderTop: "1px dashed rgba(255,193,69,0.4)", color: "#94A3B8" }}
        >
          {footnote}
        </div>
      )}
    </div>
  );
}
